import { utf8 } from './bytes.js';

/**
 * One-time feature check: does this browser expose a functional Web Share API
 * for files? Safari (mac/iOS) and Chrome Android yes; desktop Chromium partial;
 * Firefox no. We probe with a dummy octet-stream file since that's the narrowest
 * shareable MIME on most browsers.
 */
export const CAN_SHARE_FILES: boolean = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.canShare !== 'function') return false;
  try {
    const probe = new File([new Uint8Array(1)], 'probe.bin', { type: 'application/octet-stream' });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
})();

/**
 * Trigger the OS share sheet for a single file. Accepts either raw bytes or a
 * UTF-8 string (PEM files are strings). Silently no-ops on cancel or failure.
 */
export async function shareFile(
  bytes: Uint8Array | string,
  name: string,
  mime: string,
): Promise<void> {
  try {
    const data = typeof bytes === 'string' ? utf8(bytes) : bytes;
    const f = new File([data as BlobPart], name, { type: mime });
    if (navigator.canShare?.({ files: [f] })) {
      await navigator.share({ files: [f], title: name });
    }
  } catch {
    /* user cancel or platform failure — no UI noise */
  }
}

/**
 * Share a blob URL (typically one produced by URL.createObjectURL earlier in the
 * session). Fetches the blob back from the URL — this is local and essentially
 * free — then hands it to the Web Share API.
 */
export async function shareBlobUrl(url: string, name: string, mime: string): Promise<void> {
  try {
    const blob = await fetch(url).then((r) => r.blob());
    const f = new File([blob], name, { type: mime });
    if (navigator.canShare?.({ files: [f] })) {
      await navigator.share({ files: [f], title: name });
    }
  } catch {
    /* user cancel, URL revoked, or platform failure — no UI noise */
  }
}

/**
 * Attach a Chromium DownloadURL to a drag event. When the user drops the drag
 * source onto the OS shell (Finder, Explorer), the file is saved. No-op in
 * browsers that don't support DownloadURL (Firefox). `url` must be a blob URL
 * that remains valid for the lifetime of the drag.
 */
export function setDragDownloadUrl(
  ev: DragEvent,
  url: string | null | undefined,
  name: string,
  mime: string,
): void {
  if (!url || !ev.dataTransfer) return;
  try {
    ev.dataTransfer.effectAllowed = 'copy';
    ev.dataTransfer.setData('DownloadURL', `${mime}:${name}:${url}`);
  } catch {
    /* Firefox silently ignores DownloadURL */
  }
}
